/**
 * A titled group inside a card or a detail.
 *
 * The trailing slot is one of exactly three places a bound may be stated
 * (`3 of 100`), and it is the one for a bound on a VISIBLE list. A count that
 * bounds the whole projection belongs in the set header's own bounds line; a
 * count of things hidden behind a chip row belongs in that row's own `+n`.
 * Nowhere else.
 */
"use client";
import type { ReactElement, ReactNode } from "react";
import { Cbutton } from "@/components/ui/Cbutton";
import { ButtonPreset } from "@/constants/componentPresets";
import { useCrcTouchExpansion } from "./cresultcard.touch";
import {
  CRC_R_INSET,
  CRC_TEXT_FAINT,
  CRC_TYPE_STYLE,
} from "./cresultcard.tokens";
import { CresultcardTextStep } from "./cresultcard.types";
import { useCresultcardViewport } from "./cresultcard.viewport";

const SEE_ALL_TEXT_SIZE = 10;
const SEE_ALL_ROOT_ATTRIBUTES = {
  "data-el": "Cresultcard/SectionHeader/SeeAllTrigger",
  "data-crc-control": true,
};

export interface CresultcardSeeAll {
  readonly label: string;
  readonly onPress: () => void;
}

interface CresultcardSectionHeaderProps {
  readonly title: string;
  readonly wrapTitle?: boolean;
  /** A bound on the list below it, and nothing else. */
  readonly trailing?: ReactNode;
  readonly seeAll?: CresultcardSeeAll;
}

export function CresultcardSectionHeader({
  title,
  wrapTitle = false,
  trailing,
  seeAll,
}: CresultcardSectionHeaderProps): ReactElement {
  const { isMobile } = useCresultcardViewport();
  const seeAllTouch = useCrcTouchExpansion({ isMobile });

  return (
    <div
      data-el="Cresultcard/SectionHeader/Root"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        minWidth: 0,
      }}
    >
      <span
        style={{
          ...CRC_TYPE_STYLE[CresultcardTextStep.Eyebrow],
          flex: wrapTitle ? "0 1 auto" : "0 0 auto",
          ...(wrapTitle ? { minWidth: 0, overflowWrap: "anywhere" as const } : {}),
          fontSize: 8.5,
          letterSpacing: "0.16em",
          color: CRC_TEXT_FAINT,
        }}
      >
        {title}
      </span>
      <span
        aria-hidden
        style={{
          flex: "1 1 auto",
          height: 1,
          backgroundColor: "rgba(255,255,255,0.055)",
        }}
      />
      {trailing}
      {seeAll != null && (
        <Cbutton
          ariaLabel={seeAll.label}
          disableClickMotion
          label={seeAll.label}
          onClick={seeAll.onPress}
          padding="2px 8px"
          preset={ButtonPreset.Ghost}
          radius={CRC_R_INSET}
          rootAttributes={{
            ...SEE_ALL_ROOT_ATTRIBUTES,
          }}
          rootRef={seeAllTouch.measureRoot}
          // SPEC §7.4's floor, taken as padding on the root so the header row
          // keeps its height. What the expansion reaches over is the gap above
          // and below a one-line header, and neither holds a control.
          style={seeAllTouch.style}
          textSize={SEE_ALL_TEXT_SIZE}
        />
      )}
    </div>
  );
}
